/**
 * Scientific Color Ramps for Satellite Imagery Visualization
 *
 * Based on standard scientific color schemes:
 * - Viridis: Perceptually uniform, colorblind-friendly (Biomass)
 * - RdYlGr: Diverging scheme for change detection (Biomass Change)
 */

/**
 * Viridis color ramp for biomass visualization (1-50 Mg/ha)
 *
 * Scientific color scheme developed by Stéfan van der Walt and Nathaniel Smith
 * for matplotlib. Perceptually uniform, colorblind-safe, and grayscale-printable.
 *
 * Control points extracted from official Viridis palette (256 colors)
 * Purple → Blue → Cyan → Green → Yellow
 */
const VIRIDIS_CONTROL_POINTS = [
  { value: 0.0, color: [68, 1, 84] }, // Deep purple (low biomass)
  { value: 0.25, color: [59, 82, 139] }, // Blue
  { value: 0.5, color: [33, 145, 140] }, // Cyan-green
  { value: 0.75, color: [94, 201, 98] }, // Green
  { value: 1.0, color: [253, 231, 37] }, // Bright yellow (high biomass)
];

/**
 * RdYlGr (Red-Yellow-Green) diverging color ramp for biomass change (-15 to +15 Mg/ha/y)
 *
 * Based on ColorBrewer diverging scheme, designed for change detection
 * Red = Loss, Yellow = Neutral, Green = Gain
 */
const RDYLGR_CONTROL_POINTS = [
  { value: 0.0, color: [215, 48, 39] }, // Red (maximum loss -15)
  { value: 0.25, color: [252, 141, 89] }, // Orange-red
  { value: 0.5, color: [255, 255, 191] }, // Yellow (neutral 0)
  { value: 0.75, color: [145, 207, 96] }, // Light green
  { value: 1.0, color: [26, 150, 65] }, // Dark green (maximum gain +15)
];

/**
 * Linear interpolation between two values
 */
function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

/**
 * Interpolate RGB color at given position in control points array
 */
function interpolateColor(
  controlPoints: Array<{ value: number; color: number[] }>,
  position: number,
): number[] {
  // Clamp position to [0, 1]
  position = Math.max(0, Math.min(1, position));

  // Find surrounding control points
  let lower = controlPoints[0];
  let upper = controlPoints[controlPoints.length - 1];

  for (let i = 0; i < controlPoints.length - 1; i++) {
    if (
      position >= controlPoints[i].value &&
      position <= controlPoints[i + 1].value
    ) {
      lower = controlPoints[i];
      upper = controlPoints[i + 1];
      break;
    }
  }

  // Calculate local interpolation factor
  const range = upper.value - lower.value;
  const localT = range === 0 ? 0 : (position - lower.value) / range;

  // Interpolate RGB channels
  return [
    Math.round(lerp(lower.color[0], upper.color[0], localT)),
    Math.round(lerp(lower.color[1], upper.color[1], localT)),
    Math.round(lerp(lower.color[2], upper.color[2], localT)),
  ];
}

/**
 * Get Viridis color for biomass value (1-50 Mg/ha)
 *
 * @param value - Biomass in Mg/ha (megagrams per hectare = tonnes per hectare)
 * @param min - Minimum value (default: 1)
 * @param max - Maximum value (default: 50)
 * @returns RGB color array [r, g, b] (0-255)
 */
export function viridisColor(value: number, min = 1, max = 50): number[] {
  // Normalize value to [0, 1]
  const normalized = (value - min) / (max - min);
  return interpolateColor(VIRIDIS_CONTROL_POINTS, normalized);
}

/**
 * Get RdYlGr color for biomass change value (-15 to +15 Mg/ha/y)
 *
 * @param value - Biomass change in Mg/ha/year
 * @param min - Minimum value (default: -15)
 * @param max - Maximum value (default: +15)
 * @returns RGB color array [r, g, b] (0-255)
 */
export function rdylgrColor(value: number, min = -15, max = 15): number[] {
  // Normalize value to [0, 1]
  const normalized = (value - min) / (max - min);
  return interpolateColor(RDYLGR_CONTROL_POINTS, normalized);
}

/**
 * Convert RGB array to CSS color string
 */
export function rgbToString(rgb: number[]): string {
  return `rgb(${rgb[0]}, ${rgb[1]}, ${rgb[2]})`;
}

/**
 * Convert RGB array to hex color string
 */
export function rgbToHex(rgb: number[]): string {
  return "#" + rgb.map((c) => c.toString(16).padStart(2, "0")).join("");
}

/**
 * Generate color scale legend data for visualization
 *
 * @param type - "viridis" or "rdylgr"
 * @param steps - Number of color steps (default: 10)
 */
export function generateLegendSteps(
  type: "viridis" | "rdylgr",
  steps = 10,
): Array<{ value: number; color: string; label: string }> {
  const colorFn = type === "viridis" ? viridisColor : rdylgrColor;
  const [min, max] = type === "viridis" ? [1, 50] : [-15, 15];
  const unit = type === "viridis" ? "Mg/ha" : "Mg/ha/y";

  return Array.from({ length: steps }, (_, i) => {
    const value = min + (max - min) * (i / (steps - 1));
    const rgb = colorFn(value, min, max);
    return {
      value,
      color: rgbToString(rgb),
      label: `${value.toFixed(1)} ${unit}`,
    };
  });
}
